import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { url } from "../Utils/BaseUrl.js";

/**
 * Auth is carried by an httpOnly `token` cookie that the API sets on sign-in, so
 * the JWT is never exposed to JavaScript (XSS-safe). `clearAuth` only scrubs any
 * legacy tokens left in web storage by the previous header-based scheme; the
 * cookie itself is cleared server-side via POST /auth/logout.
 */
export const clearAuth = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('token');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('email');
};

const rawBaseQuery = fetchBaseQuery({
    baseUrl: `${url}`,
    credentials: 'include',
});

// Shared in-flight refresh so concurrent 401s trigger only one /auth/refresh.
let refreshing = null;

/**
 * Wraps the base query with silent session refresh. On a 401 (expired access
 * token) it calls POST /auth/refresh once — which mints a new access-token
 * cookie from the long-lived refresh cookie — then retries the original request.
 * If the refresh itself fails, the session is truly over: scrub any legacy
 * storage and bounce to /login. Auth endpoints are exempt to avoid loops.
 */
const baseQueryWithAuth = async (args, api, extraOptions) => {
    let result = await rawBaseQuery(args, api, extraOptions);

    const reqUrl = typeof args === 'string' ? args : args?.url || '';
    const isAuthCall = /auth\/(sign-in|doctor-sign-in|refresh|logout)/.test(reqUrl);

    if (result.error?.status === 401 && !isAuthCall) {
        if (!refreshing) {
            refreshing = rawBaseQuery({ url: 'auth/refresh', method: 'POST' }, api, extraOptions)
                .finally(() => { refreshing = null; });
        }
        const refreshResult = await refreshing;

        if (!refreshResult.error) {
            result = await rawBaseQuery(args, api, extraOptions);
        } else {
            clearAuth();
            if (window.location.pathname !== '/login') {
                window.location.href = '/login';
            }
        }
    }
    return result;
};

export const baseApi = createApi({
    reducerPath: 'medicalStartup',
    baseQuery: baseQueryWithAuth,
    endpoints: () => ({}),
    tagTypes: [
        "auth",
        "category",
        "doctor",
        "banner",
        "appointment",
        "dashboard",
        "faq",
        "notification",
        "payment",
        "settings",
        "users",
        "country",
        "role",
        "admin",
        "platformConfig",
        "review",
        "callHistory",
        "survey",
        "intake",
        "verification",
        "regulator",
    ],
})
